import { useQueryClient } from "@tanstack/react-query";
import { FileDropList, savedToDraft, type DraftFile } from "@/components/docs/FileDropList";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import {
  type CustomerContactDto,
  type CustomerDto,
  type CurrentPriceDto,
  contactSnapshotName,
  defaultCurrencyFor,
  piTotalMinor,
  vnDate,
} from "@hisense/shared";
import type { DescriptionLang } from "@hisense/shared";
import { PageBody, PageHeader } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import {
  LineItemsEditor,
  blankLine,
  draftsToInput,
  linesSubtotal,
  type LineDraft,
} from "@/components/docs/LineItemsEditor";
import { emptyPiHeader, piSideColumnsUsed, shippingFilledCount } from "@/components/docs/piFormLayout";
import { FormGroup, Hint, MoreBlock } from "@/components/ui/FormLayout";
import { PrintLanguageToggle } from "@/components/docs/PrintLanguageToggle";
import { ProductPickerPanel } from "@/components/docs/ProductPickerPanel";
import { formatMoney, unitPriceToInput } from "@/domain/money";
import { useAuth } from "@/app/auth";
import { useCan, useCanCreateRedPi, useCanMarkAdminOnly, useMenuAccess } from "@/app/access";
import { getLedger, setLedger, useLedger } from "@/app/ledger";
import {
  useCreatePI,
  useCompanies,
  useCustomer,
  useCustomers,
  useProformaInvoice,
  useSettings,
  useUpdatePI,
  useUserOptions,
  usePiAttachments,
} from "@/api/hooks";
import { apiFetch, ApiClientError, getLocalizedError } from "@/lib/api";
import {
  parseMoneyMajor,
  parsePercent,
  percentToInput,
} from "@/lib/moneyInput";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { QuickCreatePartyModal } from "@/components/domain/QuickCreatePartyModal";
import { contactOptionLabel } from "@/components/customers/contactOption";

const toDate = (ms: number | null | undefined) =>
  ms ? vnDate(ms) : "";
const fromDate = (s: string) =>
  s ? new Date(s + "T00:00:00Z").getTime() : null;

/** Currency codes the legacy data uses; a configured option list wins. */
const FALLBACK_CURRENCIES = ["USD"];

/**
 * The contact snapshot the document actually prints — a multi-line blob, built
 * exactly like the legacy buildContactString(). The address lives on the
 * customer, not the contact.
 */
function buildContactSnapshot(
  contact: CustomerContactDto,
  customer: CustomerDto | undefined,
): string {
  return [
    contact.name,
    customer?.address && `Address: ${customer.address}`,
    contact.phone1 && `Phone: ${contact.phone1}`,
    contact.phone2 && `Mobile: ${contact.phone2}`,
    contact.email && `Email: ${contact.email}`,
    contact.wechat && `WeChat: ${contact.wechat}`,
  ]
    .filter(Boolean)
    .join("\n");
}

/** Which contact a stored snapshot came from — matched on its first line. */
const contactIdOf = (snapshot: string, contacts: CustomerContactDto[]) =>
  contacts.find(
    (c) =>
      c.name === contactSnapshotName(snapshot) &&
      (!c.phone1 || snapshot.includes(`Phone: ${c.phone1}`)) &&
      (!c.email || snapshot.includes(`Email: ${c.email}`)),
  )?.id ?? (snapshot ? "snapshot" : "");


/** Remounts on id change so no form state can bleed between cached records. */
export function ProformaInvoiceEditorPage() {
  const { id } = useParams();
  return <PiEditor key={id ?? "new"} id={id} />;
}

function PiEditor({ id }: { id: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search: context, state: navState } = useLocation();
  const initialized = useRef(false);
  const dirty = useRef(false);
  // Người lập đã tự chọn tiền tệ — từ đó không tự đổi theo công ty của khách nữa.
  const currencyTouched = useRef(false);
  const [currencyFromCompany, setCurrencyFromCompany] = useState<{ currency: string; region: "vietnam" | "china" } | null>(null);
  const allowNavigation = useRef(false);
  const writeLock = useRef(false);
  const [creatingCustomer, setCreatingCustomer] = useState<
    ((id: string) => void) | null
  >(null);
  const { user } = useAuth();
  const can = useCan();
  const isNew = !id || id === "new";
  const {
    data: existing,
    isLoading,
    error: loadError,
    refetch,
  } = useProformaInvoice(isNew ? undefined : id);
  /**
   * Lý do sửa một PI đã xuất hóa đơn.
   *
   * Máy chủ chỉ nhận sửa khi có lý do (>= 5 ký tự) và ghi lại vết. Hiện ô này
   * TRƯỚC khi người dùng bấm Lưu, không để họ gõ xong cả trang rồi mới bị từ
   * chối.
   */
  const [amendReason, setAmendReason] = useState("");
  // Bằng chứng đặt hàng của khách (v1.8 mục 4.3): nạp danh sách đã lưu một lần; sửa xong thì ghi SAU khi PI lưu thành công.
  const queryClient = useQueryClient();
  const attachments = usePiAttachments(isNew ? undefined : id);
  // Vừa tạo PI mà lưu tệp lỗi: trang tạo chuyển sang trang sửa và mang theo tệp chưa lưu + lỗi — không để người dùng tưởng đã lưu đủ.
  const carried = (navState as { pendingEvidence?: DraftFile[] } | null)?.pendingEvidence;
  const [evidence, setEvidence] = useState<DraftFile[]>(() => carried ?? []);
  const evidenceDirty = useRef(Boolean(carried?.length));
  const [uploading, setUploading] = useState(0);
  const [savingEvidence, setSavingEvidence] = useState(false);
  // Danh sách đã lưu phải TẢI XONG mới được sửa: lưu là thay cả danh sách, nên sửa trên một danh sách rỗng vì chưa tải / tải lỗi là
  // xoá mất tệp cũ. PI mới thì chưa có gì để tải.
  const evidenceReady = isNew || attachments.isSuccess;
  // PUT tệp đi theo ô Sửa của PI (kể cả lúc vừa tạo) — tài khoản chỉ được Tạo thì báo rõ, không để tệp rơi im lặng.
  const piMenu = useMenuAccess("proformaInvoices");
  const canAttach = piMenu("update");
  useEffect(() => {
    // Đang sửa dở (kể cả tệp mang sang từ trang tạo) thì không ghi đè bằng bản tải lại.
    if (attachments.data && !evidenceDirty.current) setEvidence(attachments.data.map(savedToDraft));
  }, [attachments.data]);
  /** Chỉ quản trị viên thấy chứng từ này — tick một lần lúc tạo, không gỡ được. */
  const [adminOnly, setAdminOnly] = useState(false);
  // Ô này chỉ quản trị viên thấy (11/10/2026): người khác tick vào là tự làm mất PI mình vừa lập — máy chủ cũng từ chối (403).
  const canMarkAdminOnly = useCanMarkAdminOnly();
  // Red PI (mục 9): ở sổ Red, PI mới luôn là Red; ở sổ thường thì người có ô Tạo Red PI tick được.
  const ledger = useLedger();
  const canCreateRed = useCanCreateRedPi();
  const [redTick, setRedTick] = useState(false);
  const isRed = isNew && (ledger === "red" || (canCreateRed && redTick));
  const customers = useCustomers({});
  const companies = useCompanies();
  const settings = useSettings();
  const create = useCreatePI();
  const update = useUpdatePI(id ?? "new");
  const canEdit = user
    ? can(isNew ? "records:create" : "records:update")
    : false;

  const [h, setH] = useState(emptyPiHeader);

  /**
   * Chọn sẵn công ty thu tiền MẶC ĐỊNH cho chứng từ mới.
   *
   * Đây là dữ liệu in ra khối ngân hàng trên bản gửi khách. Bỏ trống thì lưu ra
   * một PI không có thông tin chuyển tiền — khách nhận được tờ giấy không biết
   * trả vào đâu. Chỉ điền khi đang tạo mới và người dùng chưa tự chọn.
   */
  useEffect(() => {
    if (!isNew || h.paymentCompanyId || dirty.current) return;
    const def = (companies.data ?? []).find((c) => c.isDefault);
    if (def)
      setH((prev) =>
        prev.paymentCompanyId ? prev : { ...prev, paymentCompanyId: def.id },
      );
  }, [isNew, companies.data, h.paymentCompanyId]);
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [error, setError] = useState<string | null>(() => (carried?.length ? t("evidence.saveFailed") : null));
  const [priceReview, setPriceReview] = useState(false);
  const [repricing, setRepricing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Cột phụ của bảng dòng hàng: bấm để hiện khi cần nhập; dòng nào đang dùng thì luôn hiện (piFormLayout). */
  const [sideOpen, setSideOpen] = useState(false);
  /** Mục Vận chuyển mở sẵn khi PI mở ra đã có dữ liệu vận chuyển — chốt một lần lúc nạp, không theo từng phím gõ. */
  const shippingOpenAtLoad = useRef<boolean | null>(null);
  /**
   * P-Process. On an existing PI this is whatever was snapshotted at create; on
   * a new one it follows the chosen customer's default until the user touches
   * it, after which their choice wins (touched = they overrode the default).
   */
  const [pProcess, setPProcess] = useState(false);
  const [printLanguage, setPrintLanguage] = useState<DescriptionLang>("en");
  const [pProcessTouched, setPProcessTouched] = useState(false);

  // Contacts hang off the party customers, so each party loads its own detail.
  const billToDetail = useCustomer(h.billTo);
  const notifyDetail = useCustomer(h.notifyParty);
  const consigneeDetail = useCustomer(h.consignee);
  const contactsOf = (d: typeof billToDetail) => d.data?.contacts ?? [];

  // Creating: mirror the customer's default so the tick matches what the server
  // would snapshot anyway. Stops once the user decides for themselves.
  useEffect(() => {
    if (pProcessTouched) return;
    const d = billToDetail.data;
    if (d) setPProcess(d.pProcessDefault);
  }, [
    billToDetail.data?.id,
    billToDetail.data?.pProcessDefault,
    pProcessTouched,
  ]);
  const customerById = (cid: string) =>
    [
      billToDetail.data,
      notifyDetail.data,
      consigneeDetail.data,
      ...(customers.data?.data ?? []),
    ].find((c) => c?.id === cid);

  const optionLists = settings.data?.businessOperations.optionLists;
  const currencies = optionLists?.currencies?.length
    ? optionLists.currencies
    : FALLBACK_CURRENCIES;
  // Người phụ trách (yêu cầu v1.8 mục 4.2, 11/10/2026): nhân viên ĐANG hoạt động được lập / sửa PI và thấy được công ty của khách
  // trên PI — thay danh sách gõ tay "Người lập báo giá" trong Cài đặt (production chỉ còn Louis Chen và Stephen Le). Máy chủ tính
  // lại mỗi lần đọc, nên thêm người, khoá tài khoản hay đổi ô tick là danh sách đổi theo. Vẫn lưu TÊN như trước (`resp_by` là chữ):
  // phân tích "Doanh thu theo người phụ trách", bản in, tệp xuất đọc tên này.
  const billToRegion = customerById(h.billTo)?.classification;
  const respByUsers = useUserOptions(billToRegion, { editorsOf: "proformaInvoices" });
  const respByOptions = [...new Set((respByUsers.data ?? []).map((u) => u.displayName).filter(Boolean))];
  const shippingMarkOptions = optionLists?.shippingMarks ?? [];
  const unitOptions = optionLists?.productUnits ?? [];

  useEffect(() => {
    if (!existing || initialized.current) return;
    initialized.current = true;
    setPProcess(existing.pProcess);
    setPrintLanguage(existing.printLanguage);
    setPProcessTouched(true); // an existing document owns its own value
    const loaded = {
      billTo: existing.billToId ?? "",
      shipTo: existing.shipTo ?? "",
      notifyParty: existing.notifyPartyId ?? "",
      notifyPartyContact: existing.notifyPartyContact ?? "",
      consignee: existing.consigneeId ?? "",
      consigneeContact: existing.consigneeContact ?? "",
      customerPoNo: existing.customerPoNo ?? "",
      customerDept: existing.customerDept ?? "",
      date: toDate(existing.date),
      deliveryDate: toDate(existing.deliveryDate),
      shippingMode: existing.shippingMode ?? "",
      portOfLoading: existing.portOfLoading ?? "",
      countryOfOrigin: existing.countryOfOrigin ?? "",
      dnNo: existing.dnNo ?? "",
      paymentTerms: existing.paymentTerms ?? "",
      currency: existing.currency,
      respBy: existing.respBy ?? "",
      paymentCompanyId: existing.paymentCompanyId ?? "",
      shippingMarks: existing.shippingMarks ?? "",
      remarks: existing.remarks ?? "",
      upcharge: existing.upcharge ? unitPriceToInput(existing.upcharge) : "",
      discount: percentToInput(existing.discount),
    };
    shippingOpenAtLoad.current = shippingFilledCount(loaded) > 0;
    setH(loaded);
    setLines(
      existing.items.length
        ? existing.items.map((it) => ({
            id: it.id,
            productId: it.productId ?? undefined,
            productCode: it.productCode ?? "",
            subRef: it.subRef ?? "",
            customerStyleNo: it.customerStyleNo ?? "",
            description: it.description ?? "",
            descriptionZh: it.descriptionZh ?? "",
            color: it.color ?? "",
            size: it.size ?? "",
            unit: it.unit ?? "",
            quantity: String(it.quantity),
            moq: it.moq != null ? String(it.moq) : "",
            mcq: it.mcq != null ? String(it.mcq) : "",
            msq: it.msq != null ? String(it.msq) : "",
            unitPrice: unitPriceToInput(it.unitPrice),
            dtdPrice: it.dtdPrice != null ? unitPriceToInput(it.dtdPrice) : "",
            exwPrice: it.exwPrice != null ? unitPriceToInput(it.exwPrice) : "",
            underMoqPrice:
              it.underMoqPrice != null
                ? unitPriceToInput(it.underMoqPrice)
                : "",
            // Bỏ sót dòng này thì mỗi lần mở PI ra sửa rồi lưu là XOÁ TRẮNG số LOT của
            // mọi dòng hàng: lưu là DELETE-INSERT toàn bộ dòng, trường nào form không
            // nạp lại thì coi như người dùng đã xoá. Mà LOT chính là thứ P-Process tồn
            // tại để làm — mất nó là mất luôn khả năng truy ngược lô hàng lỗi.
            lotNumber: it.lotNumber ?? "",
            imageUrl: it.imageUrl,
            fxApplied: existing.priceSnapshots?.find((s) => s.lineId === it.id)?.fx,
          }))
        : [blankLine()],
    );
  }, [existing]);

  /** Legacy handleCustomerChange: the bill-to party seeds notify + consignee. */
  const onBillToChange = (customerId: string) => {
    dirty.current=true;
    if (lines.some((l) => l.productCode || l.description)) setPriceReview(true);
    const customer = customerById(customerId);
    // PI MỚI lấy tiền tệ mặc định của công ty khách (yêu cầu v1.8 mục 6.2): Trung Quốc RMB, Việt Nam USD — theo Cài đặt. Chỉ khi
    // người lập chưa tự chọn tiền tệ và chưa có dòng nào mang giá: không bao giờ âm thầm đổi tiền tệ dưới các con số đã gõ.
    const region = customer?.classification;
    const companyCurrency =
      // "Chưa có giá" là MỌI ô tiền: đơn giá, DTD, EXW, giá dưới MOQ của từng dòng và phụ phí — đổi nhãn tiền tệ dưới bất kỳ con số
      // nào đã gõ là âm thầm đổi giá.
      !existing && region && !currencyTouched.current && !h.upcharge.trim() &&
      !lines.some((l) => [l.unitPrice, l.dtdPrice, l.exwPrice, l.underMoqPrice].some((v) => (v ?? "").trim()))
        ? defaultCurrencyFor(region, settings.data?.system.defaultCurrencyByRegion, settings.data?.system.defaultCurrency ?? "USD")
        : null;
    setCurrencyFromCompany(companyCurrency && region ? { currency: companyCurrency, region } : null);
    setH((prev) => ({
      ...prev,
      ...(companyCurrency ? { currency: companyCurrency } : {}),
      billTo: customerId,
      shipTo: "",
      notifyParty: customerId,
      notifyPartyContact: "",
      consignee: customerId,
      consigneeContact: "",
      customerDept: "",
      paymentTerms: customer?.paymentTerm ?? "",
    }));
  };

  /** Legacy handleContactChange: department + terms + the mirrored snapshots. */
  const onBillToContactChange = (contactId: string) => {
    dirty.current=true;
    const contact = contactsOf(billToDetail).find((c) => c.id === contactId);
    if (!contact) {
      setH((prev) => ({ ...prev, shipTo: "" }));
      return;
    }
    const snapshot = buildContactSnapshot(contact, customerById(h.billTo));
    setH((prev) => ({
      ...prev,
      shipTo: snapshot,
      customerDept: contact.section ?? "",
      // customer_contacts has no payment_term column; only the customer carries one.
      paymentTerms: customerById(h.billTo)?.paymentTerm ?? prev.paymentTerms,
      ...(prev.notifyParty === h.billTo
        ? { notifyPartyContact: snapshot }
        : {}),
      ...(prev.consignee === h.billTo ? { consigneeContact: snapshot } : {}),
    }));
  };

  const onPartyContactChange = (
    field: "notifyPartyContact" | "consigneeContact",
    contacts: CustomerContactDto[],
    customerId: string,
    contactId: string,
  ) => {
    const contact = contacts.find((c) => c.id === contactId);
    setH((prev) => ({
      ...prev,
      [field]: contact
        ? buildContactSnapshot(contact, customerById(customerId))
        : "",
    }));
  };

  const upcharge = parseMoneyMajor(h.upcharge);
  const discount = parsePercent(h.discount);
  // Tổng xem trước tính theo ô đang gõ (dòng sai = 0), khớp số Thành tiền của từng dòng; lưu thì máy chủ tính lại.
  const subtotal = linesSubtotal(lines);
  const sideUsed = piSideColumnsUsed(lines);
  const showSide = sideOpen || sideUsed;
  const grandTotal = piTotalMinor(
    subtotal,
    upcharge.minor ?? 0,
    discount.value ?? 0,
  );

  usePageCrumbs(
    isNew
      ? [{ label: t("pi.newPi") }]
      : existing
        ? [
            {
              label: existing.piNo,
              to: `/proforma-invoices/${existing.id}${context}`,
            },
            { label: t("common.edit") },
          ]
        : [{ label: t("common.edit") }],
    `/proforma-invoices${context}`,
  );

  const busy = create.isPending || update.isPending || repricing || savingEvidence;
  const blocker = useBlocker(
    () => !allowNavigation.current && (dirty.current || writeLock.current),
  );
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (!allowNavigation.current && (dirty.current || writeLock.current)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, []);
  if (!isNew && isLoading)
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  if (!isNew && (loadError || !existing))
    return (
      <PageBody>
        <ErrorState
          message={t("pi.errorLoad")}
          onRetry={() => void refetch()}
        />
      </PageBody>
    );

  /** Ghi danh sách tệp bằng chứng khi có đổi. PI đã lưu mà tệp chưa lưu thì báo rõ, không im lặng. */
  const saveEvidence = async (piId: string): Promise<boolean> => {
    if (!evidenceDirty.current) return true;
    if (!canAttach) {
      setError(t("evidence.needEditRight"));
      return false;
    }
    setSavingEvidence(true);
    try {
      await apiFetch(`/proforma-invoices/${piId}/attachments`, {
        method: "PUT",
        body: {
          attachments: evidence.map((f) =>
            f.id
              ? { id: f.id, kind: f.kind, fileName: f.fileName, fileSize: f.fileSize, mimeType: f.mimeType }
              : { key: f.key, kind: f.kind, fileName: f.fileName, fileSize: f.fileSize, mimeType: f.mimeType },
          ),
        },
      });
      evidenceDirty.current = false;
      await queryClient.invalidateQueries({ queryKey: ["pi-attachments", piId] });
      return true;
    } catch {
      setError(t("evidence.saveFailed"));
      return false;
    } finally {
      setSavingEvidence(false);
    }
  };

  const save = async () => {
    if (!canEdit || writeLock.current || repricing) return;
    if (uploading > 0) {
      setError(t("evidence.waitUpload"));
      return;
    }
    if (priceReview) {
      setError(t("piWorkspace.reviewPrices"));
      return;
    }
    setError(null);
    const conv = draftsToInput(lines);
    if ("error" in conv) {
      setError(
        t("common.lineError", {
          line: conv.error.line,
          field: t(`common.${conv.error.field}`),
        }),
      );
      return;
    }
    if (upcharge.invalid) {
      setError(t("pi.validAmount"));
      return;
    }
    if (discount.invalid) {
      setError(t("pi.validAmount"));
      return;
    }

    const payload = {
      ...(existing?.hasInvoice && amendReason.trim() ? { amendReason: amendReason.trim() } : {}),
      // Chỉ gửi khi TẠO MỚI: bản cập nhật không mang cờ này, và máy chủ cũng
      // không cho gỡ.
      ...(isNew && adminOnly ? { adminOnly: true } : {}),
      ...(isRed ? { isRed: true } : {}),
      pProcess,
      printLanguage,
      billTo: h.billTo,
      shipTo: h.shipTo,
      notifyParty: h.notifyParty,
      notifyPartyContact: h.notifyPartyContact,
      consignee: h.consignee,
      consigneeContact: h.consigneeContact,
      customerPoNo: h.customerPoNo,
      customerDept: h.customerDept,
      date: fromDate(h.date) ?? undefined,
      deliveryDate: fromDate(h.deliveryDate),
      shippingMode: h.shippingMode,
      portOfLoading: h.portOfLoading,
      countryOfOrigin: h.countryOfOrigin,
      dnNo: h.dnNo,
      paymentTerms: h.paymentTerms,
      currency: h.currency,
      respBy: h.respBy,
      paymentCompanyId: h.paymentCompanyId,
      shippingMarks: h.shippingMarks,
      remarks: h.remarks,
      upcharge: upcharge.minor ?? 0,
      discount: discount.value ?? 0,
      items: conv.items,
    };
    writeLock.current = true;
    try {
      if (isNew) {
        const r = await create.mutateAsync(payload);
        allowNavigation.current = true;
        if (!(await saveEvidence(r.data.id))) {
          // Trang sửa là một lần mount mới: mang tệp chưa lưu sang để báo lỗi và cho lưu lại (ảnh xem trước đã thu hồi cùng trang này).
          navigate(`/proforma-invoices/${r.data.id}/edit${context}`, {
            replace: true,
            state: { pendingEvidence: evidence.filter((f) => !f.id).map((f) => ({ ...f, localUrl: undefined })) },
          });
          return;
        }
        // PI vừa lập là Red PI mà đang ở sổ thường: chuyển sang sổ Red, không thì mở chi tiết sẽ 404.
        if (r.data.isRed && getLedger() !== "red") {
          setLedger("red");
          await queryClient.resetQueries();
        }
        navigate(`/proforma-invoices/${r.data.id}${context}`);
      } else {
        await update.mutateAsync(payload);
        if (!(await saveEvidence(id!))) return;
        allowNavigation.current = true;
        navigate(`/proforma-invoices/${id}${context}`);
      }
    } catch (err) {
      if (
        err instanceof ApiClientError &&
        err.code === "duplicate_customer_po"
      ) {
        setError(
          t("pi.duplicatePo", {
            po: err.fields?.po ?? h.customerPoNo,
            piNo: err.fields?.piNo ?? "",
          }),
        );
        return;
      }
      setError(getLocalizedError(err, t, "pi.errorSave"));
    } finally {
      writeLock.current = false;
    }
  };

  const contactSelect = (
    label: string,
    value: string,
    contacts: CustomerContactDto[],
    onPick: (contactId: string) => void,
    labelClassName?: string,
  ) => (
    <div>
      <FieldLabel className={labelClassName}>{label}</FieldLabel>
      <Select
        aria-label={label}
        containerClassName="w-full"
        value={value}
        onChange={(e) => onPick(e.target.value)}
      >
        <option value="">{t("pi.selectContact")}</option>
        {value === "snapshot" && (
          <option value="snapshot">{t("piWorkspace.savedContact")}</option>
        )}
        {contacts.map((c) => (
          <option key={c.id} value={c.id}>
            {contactOptionLabel(c, t)}
          </option>
        ))}
      </Select>
    </div>
  );

  const customerRows = customers.data?.data ?? [];
  /**
   * Lối tắt tạo khách ngay trong ô chọn. Giữ luôn HÀM CHỌN của ô vừa bấm, vì
   * form này có ba ô khách (Bill To / Notify / Consignee) và khách vừa tạo phải
   * rơi vào đúng ô đó.
   */

  /**
   * Ô chọn khách CÓ TÌM KIẾM, kèm địa chỉ ở dòng phụ.
   *
   * Và quan trọng hơn: id không nằm trong danh sách tải về (khách đã lưu trữ,
   * hoặc rơi ngoài trang đầu) vẫn phải hiện ra — trước đây <select> không có
   * option khớp nên nó rơi về rỗng, và lưu lại là ÂM THẦM GỠ khách khỏi chứng
   * từ. Chưa tra được tên thì hiện tên đã chụp trên chính chứng từ.
   */
  const customerSelect = (
    label: string,
    value: string,
    onPick: (id: string) => void,
    labelClassName?: string,
  ) => {
    const known = customerRows.some((c) => c.id === value);
    return (
      <div>
        <FieldLabel className={labelClassName}>{label}</FieldLabel>
        <SearchableFilter
          className="w-full"
          label={label}
          allLabel={t("pi.selectCustomer")}
          searchPlaceholder={t("pi.selectCustomer")}
          value={value}
          isLoading={customers.isLoading}
          error={customers.error ? t("customers.errorLoad") : null}
          onRetry={() => void customers.refetch()}
          options={[
            ...(value && !known
              ? [
                  {
                    value,
                    label:
                      customerById(value)?.companyName ??
                      (value === existing?.notifyPartyId
                        ? existing.notifyPartyName
                        : value === existing?.consigneeId
                          ? existing.consigneeName
                          : existing?.billToName) ??
                      t("pi.unknownCustomer"),
                    sublabel: null,
                  },
                ]
              : []),
            ...customerRows.map((c) => ({
              value: c.id,
              label: c.companyName,
              sublabel:
                [c.address, c.phone].filter(Boolean).join(" · ") || null,
            })),
          ]}
          onChange={(value)=>{dirty.current=true;onPick(value);}}
          footer={(close) => (
            <button
              type="button"
              onClick={() => {
                close();
                setCreatingCustomer(() => onPick);
              }}
              className="w-full rounded-sm px-2.5 py-1.5 text-left text-xs text-brand hover:bg-brand-tint/40"
            >
              + {t("customers.newCustomer")}
            </button>
          )}
        />
      </div>
    );
  };

  /** Ma trận Các bên: nhãn hàng + hai cột Khách hàng / Người liên hệ trên thẻ rộng; thẻ hẹp mỗi ô có nhãn riêng. */
  const wideSrOnly = "@2xl:sr-only";
  const partyRows = [
    {
      key: "billTo",
      title: t("pi.billTo"),
      customer: customerSelect(t("pi.billTo"), h.billTo, onBillToChange, wideSrOnly),
      contact: contactSelect(t("pi.billToContactPerson"), contactIdOf(h.shipTo, contactsOf(billToDetail)), contactsOf(billToDetail), onBillToContactChange, wideSrOnly),
    },
    {
      key: "notify",
      title: t("pi.notifyPartyCustomer"),
      customer: customerSelect(t("pi.notifyPartyCustomer"), h.notifyParty, (cid) => setH({ ...h, notifyParty: cid, notifyPartyContact: "" }), wideSrOnly),
      contact: contactSelect(t("pi.notifyPartyContactPerson"), contactIdOf(h.notifyPartyContact, contactsOf(notifyDetail)), contactsOf(notifyDetail),
        (cid) => onPartyContactChange("notifyPartyContact", contactsOf(notifyDetail), h.notifyParty, cid), wideSrOnly),
    },
    {
      key: "consignee",
      title: t("pi.consigneeCustomer"),
      customer: customerSelect(t("pi.consigneeCustomer"), h.consignee, (cid) => setH({ ...h, consignee: cid, consigneeContact: "" }), wideSrOnly),
      contact: contactSelect(t("pi.consigneeContactPerson"), contactIdOf(h.consigneeContact, contactsOf(consigneeDetail)), contactsOf(consigneeDetail),
        (cid) => onPartyContactChange("consigneeContact", contactsOf(consigneeDetail), h.consignee, cid), wideSrOnly),
    },
  ];
  const shippingFilled = shippingFilledCount(h);

  /*
   * Bố cục (19/09/2026, cùng chuẩn biểu mẫu sản phẩm — piFormLayout.ts): tiêu đề + tuỳ chọn chứng từ ở đầu trang; nhóm Các bên
   * (ma trận không hụt ô); nhóm Điều khoản (gập Vận chuyển, tự mở khi có dữ liệu); nhóm Hàng hoá (cột phụ, khối tổng tiền
   * thẳng hàng); Ghi chú; một thanh Hủy / Lưu dính đáy (bỏ nút Lưu thứ hai ở đầu trang).
   */
  return (
    <PageBody>
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
      <fieldset
        disabled={!canEdit || busy}
        onChangeCapture={() => {
          dirty.current = true;
        }}
        onClickCapture={(e) => {
          if (
            (e.target as HTMLElement).closest(
              '[role="option"], [aria-label="Remove line"]',
            )
          )
            dirty.current = true;
        }}
        className="min-w-0 space-y-4"
      >
        {creatingCustomer && (
          <QuickCreatePartyModal
            kind="customer"
            onClose={() => setCreatingCustomer(null)}
            onCreated={(id) => creatingCustomer(id)}
          />
        )}
        <PageHeader
          title={isNew ? t("piWorkspace.newTitle") : t("piWorkspace.editTitle")}
          subtitle={existing ? [existing.piNo, existing.billToName].filter(Boolean).join(" · ") : undefined}
          actions={
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
              <PrintLanguageToggle
                value={printLanguage}
                onChange={(lang) => { dirty.current = true; setPrintLanguage(lang); }}
                disabled={!canEdit}
              />
              <label className="flex items-center gap-2 text-xs text-ink-soft">
                <input
                  type="checkbox"
                  checked={pProcess}
                  onChange={(e) => {
                    setPProcess(e.target.checked);
                    setPProcessTouched(true);
                  }}
                  className="size-3.5 accent-[color:var(--color-brand)]"
                />
                <span className="font-medium text-ink">{t("lot.pProcess")}</span>
                <span className="hidden xl:inline">{t("lot.pProcessHint")}</span>
              </label>
              {/* Red PI (mục 9): chỉ lúc TẠO; ở sổ Red thì luôn bật và khoá. */}
              {isNew && (canCreateRed || ledger === "red") && (
                <label className="flex items-center gap-2 text-xs text-ink-soft" title={ledger === "red" ? t("redPi.tickLocked") : undefined}>
                  <input
                    type="checkbox"
                    checked={isRed}
                    disabled={ledger === "red"}
                    onChange={(e) => setRedTick(e.target.checked)}
                    className="size-3.5 accent-[color:var(--color-danger)]"
                  />
                  <span className="font-medium text-danger">{t("redPi.tick")}</span>
                  <span className="hidden xl:inline">{t("redPi.tickHint")}</span>
                </label>
              )}
              {/* Chỉ hiện lúc TẠO và chỉ với quản trị viên: tick xong là vĩnh viễn, không có đường gỡ. */}
              {isNew && canMarkAdminOnly && (
                <label className="flex items-center gap-2 text-xs text-ink-soft">
                  <input
                    type="checkbox"
                    checked={adminOnly}
                    onChange={(e) => setAdminOnly(e.target.checked)}
                    className="size-3.5 accent-[color:var(--color-brand)]"
                  />
                  <span className="font-medium text-ink">{t("pi.adminOnly")}</span>
                  <span className="hidden xl:inline">{t("pi.adminOnlyHint")}</span>
                </label>
              )}
            </div>
          }
        />

        {/* Chỉ làm mờ ô nhập thì người dùng tưởng giao diện lỗi. Nói ra lý do. */}
        {!canEdit && (
          <p className="rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">
            {t("errors.readOnly")}
          </p>
        )}

        {/* Các bên — bên mua chọn trước thì bên nhận thông báo và bên nhận hàng tự lấy theo, đúng như hệ cũ. */}
        <FormGroup id="pi-group-parties" title={t("piWorkspace.groups.parties")} hint={t("piWorkspace.groups.partiesHint")}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 @2xl:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] @2xl:items-end">
            <span className="hidden @2xl:block" aria-hidden="true" />
            <span className="hidden text-xs font-medium text-ink-soft @2xl:block" aria-hidden="true">{t("piWorkspace.partyCustomer")}</span>
            <span className="hidden text-xs font-medium text-ink-soft @2xl:block" aria-hidden="true">{t("piWorkspace.partyContact")}</span>
            {partyRows.map((row) => (
              <Fragment key={row.key}>
                <span className="hidden self-center text-sm font-medium text-ink @2xl:block" aria-hidden="true">{row.title}</span>
                {row.customer}
                {row.contact}
              </Fragment>
            ))}
          </div>
        </FormGroup>

        {priceReview && (
          <Panel className="p-3">
            <p className="text-sm text-warn">{t("piWorkspace.reviewPrices")}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setPriceReview(false)}>
                {t("piWorkspace.keepPrices")}
              </Button>
              <Button
                disabled={repricing}
                onClick={async () => {
                  setRepricing(true);
                  try {
                    const next = await Promise.all(
                      lines.map(async (line) => {
                        if (!line.productId) return line;
                        const { data } = await apiFetch<{
                          data: CurrentPriceDto;
                        }>(`/products/${line.productId}/price`, {
                          query: { customerId: h.billTo || undefined },
                        });
                        if (data.source === "none") {
                          const { data: product } = await apiFetch<{
                            data: {
                              sellingPrice: number | null;
                              currency: string;
                            };
                          }>(`/products/${line.productId}`);
                          return {
                            ...line,
                            // Giá nạp lại thay giá đã quy đổi — bản ghi quy đổi cũ không còn đúng với dòng này.
                            fxConversion: undefined,
                            fxApplied: undefined,
                            unitPrice:
                              product.currency === h.currency &&
                              product.sellingPrice != null
                                ? unitPriceToInput(product.sellingPrice)
                                : "",
                          };
                        }
                        return {
                          ...line,
                          fxConversion: undefined,
                          fxApplied: undefined,
                          unitPrice:
                            data.currency === h.currency && data.price != null
                              ? unitPriceToInput(data.price)
                              : "",
                        };
                      }),
                    );
                    setLines(next);
                    setPriceReview(false);
                    setError(null);
                  } catch (err) {
                    setError(getLocalizedError(err, t, "pi.errorLoad"));
                  } finally {
                    setRepricing(false);
                  }
                }}
              >
                {t("piWorkspace.reloadPrices")}
              </Button>
            </div>
          </Panel>
        )}

        {/* Điều khoản — hai hàng bốn ô trên thẻ rộng, không hụt ô. Bộ phận khách hàng nằm cạnh PO khách hàng (tham chiếu phía khách). */}
        <FormGroup id="pi-group-terms" title={t("piWorkspace.groups.terms")} hint={t("piWorkspace.groups.termsHint")}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 @md:grid-cols-2 @3xl:grid-cols-4">
            <div>
              <FieldLabel htmlFor="pi-date">{t("pi.date")}</FieldLabel>
              <Input id="pi-date" type="date" value={h.date} onChange={(e) => setH({ ...h, date: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="pi-po">{t("pi.poNo")}</FieldLabel>
              <Input id="pi-po" className="font-mono" value={h.customerPoNo} onChange={(e) => setH({ ...h, customerPoNo: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="pi-dept">{t("pi.customerDept")}</FieldLabel>
              <Input id="pi-dept" value={h.customerDept} onChange={(e) => setH({ ...h, customerDept: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="pi-delivery">{t("common.delivery")}</FieldLabel>
              <Input id="pi-delivery" type="date" value={h.deliveryDate} onChange={(e) => setH({ ...h, deliveryDate: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="pi-currency">{t("pi.currency")}</FieldLabel>
              <Select
                id="pi-currency"
                disabled={Boolean(existing?.payments.length)}
                containerClassName="w-full"
                value={h.currency}
                onChange={(e) => {
                  if (lines.some((l) => l.productCode || l.description)) {
                    setLines((prev) =>
                      prev.map((l) => ({
                        ...l,
                        unitPrice: "",
                        dtdPrice: "",
                        exwPrice: "",
                        underMoqPrice: "",
                        fxConversion: undefined,
                        fxApplied: undefined,
                      })),
                    );
                    setError(t("piWorkspace.currencyReset"));
                  }
                  currencyTouched.current = true;
                  setCurrencyFromCompany(null);
                  setH({ ...h, currency: e.target.value });
                }}
              >
                {!currencies.includes(h.currency) && <option value={h.currency}>{h.currency}</option>}
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
              {currencyFromCompany && currencyFromCompany.currency === h.currency ? (
                <Hint>{t("piPicker.currencyFromCompany", { currency: h.currency, company: t(currencyFromCompany.region === "china" ? "customers.china" : "customers.vietnam") })}</Hint>
              ) : null}
            </div>
            <div>
              <FieldLabel htmlFor="pi-company">{t("pi.paymentCompany")}</FieldLabel>
              <Select id="pi-company" containerClassName="w-full" value={h.paymentCompanyId} onChange={(e) => setH({ ...h, paymentCompanyId: e.target.value })}>
                <option value="">{t("pi.selectPaymentCompany")}</option>
                {(companies.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.beneficiary}</option>)}
              </Select>
              {/* Nói ngay khối ngân hàng nào sẽ được in ra — chọn nhầm công ty là khách chuyển tiền vào sai tài khoản. */}
              {!h.paymentCompanyId && <Hint tone="warn">{t("pi.paymentCompanyMissing")}</Hint>}
            </div>
            <div>
              <FieldLabel htmlFor="pi-terms">{t("customers.terms")}</FieldLabel>
              <Input id="pi-terms" value={h.paymentTerms} onChange={(e) => setH({ ...h, paymentTerms: e.target.value })} />
              {h.paymentTerms ? <Hint>{t("pi.termsAutofilled")}</Hint> : null}
            </div>
            <div>
              <FieldLabel htmlFor="pi-resp">{t("pi.respBy")}</FieldLabel>
              {respByUsers.isError ? (
                <>
                  <Input id="pi-resp" value={h.respBy} onChange={(e) => setH({ ...h, respBy: e.target.value })} />
                  <Hint>{t("pi.noRespByOptions")}</Hint>
                </>
              ) : (
                <>
                  <Select id="pi-resp" containerClassName="w-full" value={h.respBy} onChange={(e) => setH({ ...h, respBy: e.target.value })}>
                    <option value="">{t("pi.selectRespBy")}</option>
                    {/* Tên đã lưu trên PI cũ ("Amy/Stephen", người đã nghỉ) vẫn giữ — chỉ người MỚI chọn mới phải nằm trong danh sách. */}
                    {!respByOptions.includes(h.respBy) && h.respBy && <option value={h.respBy}>{t("pi.respByKept", { name: h.respBy })}</option>}
                    {respByOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                  <Hint>{t("pi.respByFromStaff")}</Hint>
                </>
              )}
            </div>
          </div>

          {/* Vận chuyển — hệ cũ gần như không dùng nên gập; PI nào có dữ liệu ở đây thì mở sẵn. */}
          <MoreBlock
            title={t("common.shipping")}
            defaultOpen={shippingOpenAtLoad.current ?? false}
            filledLabel={shippingFilled > 0 ? t("piWorkspace.filled", { count: shippingFilled }) : null}
          >
            <div className="grid grid-cols-1 gap-x-4 gap-y-4 @md:grid-cols-2 @3xl:grid-cols-4">
              <div>
                {/* Nhãn riêng: "Vận chuyển" đã là tên mục gập. */}
                <FieldLabel htmlFor="pi-ship-mode">{t("packingLists.shippingMode")}</FieldLabel>
                <Input id="pi-ship-mode" value={h.shippingMode} onChange={(e) => setH({ ...h, shippingMode: e.target.value })} />
              </div>
              <div>
                <FieldLabel htmlFor="pi-port">{t("pi.portOfLoading")}</FieldLabel>
                <Input id="pi-port" value={h.portOfLoading} onChange={(e) => setH({ ...h, portOfLoading: e.target.value })} />
              </div>
              <div>
                <FieldLabel htmlFor="pi-origin">{t("pi.countryOfOrigin")}</FieldLabel>
                <Input id="pi-origin" value={h.countryOfOrigin} onChange={(e) => setH({ ...h, countryOfOrigin: e.target.value })} />
              </div>
              <div>
                <FieldLabel htmlFor="pi-dn">{t("pi.dnNo")}</FieldLabel>
                <Input id="pi-dn" className="font-mono" value={h.dnNo} onChange={(e) => setH({ ...h, dnNo: e.target.value })} />
              </div>
              <div className="@md:col-span-2 @3xl:col-span-4">
                <FieldLabel htmlFor="pi-marks">{t("pi.shippingMarks")}</FieldLabel>
                {shippingMarkOptions.length ? (
                  <Select id="pi-marks" containerClassName="w-full" value={h.shippingMarks} onChange={(e) => setH({ ...h, shippingMarks: e.target.value })}>
                    <option value="">{t("pi.selectShippingMark")}</option>
                    {!shippingMarkOptions.includes(h.shippingMarks) && h.shippingMarks && <option value={h.shippingMarks}>{h.shippingMarks}</option>}
                    {shippingMarkOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                  </Select>
                ) : (
                  <Input id="pi-marks" value={h.shippingMarks} onChange={(e) => setH({ ...h, shippingMarks: e.target.value })} />
                )}
              </div>
            </div>
          </MoreBlock>
        </FormGroup>

        {/* Bằng chứng đặt hàng (v1.8 mục 4.3): tách khỏi ô "PO của khách" (số tham chiếu) ở nhóm trên. */}
        <FormGroup id="pi-group-evidence" title={t("evidence.piTitle")} hint={t("evidence.piHint")}>
          {!evidenceReady ? (
            attachments.isError ? (
              <div className="flex flex-wrap items-center gap-2 text-xs text-danger" role="alert">
                {t("evidence.loadFailed")}
                <Button type="button" size="sm" variant="secondary" onClick={() => void attachments.refetch()}>
                  {t("common.retry")}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-ink-faint">{t("evidence.loading")}</p>
            )
          ) : (
            <>
              {canEdit && !canAttach ? <p className="mb-1 text-xs text-ink-faint">{t("evidence.needEditRight")}</p> : null}
              <FileDropList
                files={evidence}
                folder="pi-attachments"
                readOnly={!canEdit || !canAttach}
                compact
                onBusyChange={setUploading}
                onChange={(next) => {
                  evidenceDirty.current = true;
                  dirty.current = true;
                  setEvidence(next);
                }}
              />
            </>
          )}
        </FormGroup>

        {/* Hàng hoá — cột phụ ẩn khi không dòng nào dùng; khối tổng tiền thẳng một cột dưới bảng. */}
        <FormGroup
          id="pi-group-lines"
          flush
          title={t("piWorkspace.groups.lines")}
          hint={t("piWorkspace.groups.linesHint")}
          actions={
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={sideUsed}
                title={sideUsed ? t("piWorkspace.sideLocked") : t("piWorkspace.sideColumns")}
                aria-pressed={showSide}
                onClick={() => setSideOpen((open) => !open)}
              >
                {showSide ? t("piWorkspace.sideHide") : t("piWorkspace.sideShow")}
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
                <Plus className="size-3.5" /> {t("pi.addProducts")}
              </Button>
            </>
          }
        >
          <LineItemsEditor
            lang={printLanguage}
            lines={lines}
            onChange={(next) => { dirty.current = true; setLines(next); }}
            currency={h.currency}
            variant="pi"
            unitOptions={unitOptions}
            customerId={h.billTo || undefined}
            showLot={pProcess}
            showSide={showSide}
            hideTotals
            framed={false}
            lotSuggestions={[
              ...new Set(
                lines.map((l) => (l.lotNumber ?? "").trim()).filter(Boolean),
              ),
            ]}
          />
          <div className="flex justify-end border-t border-line px-4 py-3">
            <dl className="grid w-full max-w-md grid-cols-[minmax(0,1fr)_11rem] items-center gap-x-4 gap-y-2 text-sm">
              <dt className="text-right text-ink-soft">{t("common.subtotal")}</dt>
              <dd className="text-right font-mono tabular text-ink">{formatMoney(subtotal, h.currency)}</dd>
              <dt className="text-right"><label htmlFor="pi-upcharge" className="text-ink-soft">{t("pi.upcharge")}</label></dt>
              <dd>
                <Input id="pi-upcharge" inputMode="decimal" className="font-mono tabular [&_input]:text-right" invalid={upcharge.invalid} trailing={h.currency}
                  value={h.upcharge} onChange={(e) => setH({ ...h, upcharge: e.target.value })} placeholder="0.00" />
              </dd>
              <dt className="text-right"><label htmlFor="pi-discount" className="text-ink-soft">{t("pi.discountPct")}</label></dt>
              <dd>
                <Input id="pi-discount" inputMode="decimal" className="font-mono tabular [&_input]:text-right" invalid={discount.invalid} trailing="%"
                  value={h.discount} onChange={(e) => setH({ ...h, discount: e.target.value })} placeholder="0" />
              </dd>
              <dt className="border-t border-line pt-2 text-right font-medium text-ink">{t("pi.total")} · {h.currency}</dt>
              <dd className="border-t border-line pt-2 text-right font-mono tabular text-base font-semibold text-ink">{formatMoney(grandTotal, h.currency)}</dd>
            </dl>
          </div>
        </FormGroup>

        <FormGroup id="pi-group-notes" title={t("piWorkspace.groups.notes")}>
          <textarea
            aria-label={t("common.remarks")}
            rows={3}
            value={h.remarks}
            onChange={(e) => setH({ ...h, remarks: e.target.value })}
            className="w-full rounded-sm border border-line-strong bg-surface px-2.5 py-2 text-sm text-ink outline-none focus:border-brand"
          />
        </FormGroup>

        {/* PI đã xuất hóa đơn: bắt ghi lý do NGAY, không để bấm Lưu rồi mới báo. */}
        {existing?.hasInvoice && (
          <Panel className="border-warn/30 bg-warn-tint p-4">
            <FieldLabel htmlFor="pi-amend-reason">{t("pi.amendReason")}</FieldLabel>
            <p className="mb-2 text-xs">{t("pi.amendReasonHint")}</p>
            <Input
              id="pi-amend-reason"
              value={amendReason}
              onChange={(e) => setAmendReason(e.target.value)}
            />
          </Panel>
        )}

        {error && (
          <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">
            {error}
          </p>
        )}

        {/* Một thanh Hủy / Lưu duy nhất, dính đáy vùng cuộn (19/09/2026 — bỏ nút Lưu thứ hai ở đầu trang). */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-2 rounded-md border border-line bg-surface px-4 py-3 shadow-[var(--shadow-card)]">
          <Button
            variant="secondary"
            onClick={() =>
              navigate(
                isNew
                  ? `/proforma-invoices${context}`
                  : `/proforma-invoices/${id}${context}`,
              )
            }
          >
            {t("common.cancel")}
          </Button>
          <Button disabled={!canEdit || busy || uploading > 0} onClick={save} title={uploading > 0 ? t("evidence.waitUpload") : undefined}>
            {busy ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </fieldset>
      {/* Hộp Thêm sản phẩm nằm NGOÀI fieldset: sự kiện React đi theo cây component (cả qua portal), nên gõ vào ô tìm của hộp từng
          đánh dấu PI là đã sửa — rời trang bị hỏi "Leave site?" dù không đổi gì (12/10/2026). Thêm dòng vẫn đánh dấu ở onAdd. */}
      {pickerOpen && (
        <ProductPickerPanel
          customerId={h.billTo}
          currency={h.currency}
          piId={id}
          onClose={() => setPickerOpen(false)}
          onAdd={(added) => {
            dirty.current = true;
            setLines((prev) => {
              // Drop the untouched blank starter row the editor opens with.
              const kept = prev.filter(
                (l) => l.productCode.trim() || l.description.trim(),
              );
              return [...kept, ...added];
            });
          }}
        />
      )}
    </PageBody>
  );
}
